import { useMemo } from "react";

import { useSelector } from "react-redux";

import type { CrmField } from "app/pages/sales-intelligence/domain/leads/types/filter-options";
import {
    type ICrmFieldsFilterDto,
    selectCrmFieldLabelByName,
} from "app/pages/sales-intelligence/modules/leads-search-filters";
import type { ICrmChipItem } from "app/pages/sales-intelligence/modules/leads-search-filters/types/options.dto";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import type { InclusionOption } from "app/pages/sales-intelligence/types/commonMaps";

interface IUseCrmFilterLogicProps {
    integrationName: Integration;
    fields: readonly CrmField[];
    value: ICrmFieldsFilterDto | undefined;
    inclusion: InclusionOption;
    fieldLabelsOverride?: Record<string, string>;
    onFilterUpdate: (update: ICrmFieldsFilterDto) => void;
    onFilterValueRemove?: (update: ICrmFieldsFilterDto) => void;
}

export const useCrmFilterResultLogic = ({
    integrationName,
    fields,
    value,
    inclusion,
    fieldLabelsOverride = {},
    onFilterUpdate,
    onFilterValueRemove,
}: IUseCrmFilterLogicProps) => {
    const fieldNames = useMemo(() => fields.map((f) => f.fieldName), [fields]);

    const selectorLabels = useSelector((state) => selectCrmFieldLabelByName(state, [integrationName, fieldNames]));

    const fieldLabels = useMemo(
        () => ({
            ...selectorLabels,
            ...fieldLabelsOverride,
        }),
        [selectorLabels, fieldLabelsOverride],
    );

    const onDelete = (id: string, { fieldName, isAggregated }: ICrmChipItem) => {
        const otherFilters = value?.listFieldsFilters?.filter((f) => f.fieldName !== fieldName) ?? [];

        if (isAggregated) {
            const newValue = {
                inclusion,
                listFieldsFilters: otherFilters,
            };

            onFilterValueRemove?.(newValue);
            onFilterUpdate(newValue);

            return;
        }
        const currentField = value?.listFieldsFilters?.find((f) => f.fieldName === fieldName);
        const remainingValues = currentField?.fieldValues?.filter((v) => v !== id);

        const updatedFilters = !remainingValues?.length
            ? otherFilters
            : [...otherFilters, { fieldName, fieldValues: remainingValues }];

        const newValue = {
            inclusion,
            listFieldsFilters: updatedFilters,
        };

        onFilterValueRemove?.(newValue);
        onFilterUpdate(newValue);
    };

    return {
        fieldLabels,
        onDelete,
    };
};
